import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { UserDto } from '~/types'
const { $i18n } = useNuxtApp()

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<UserDto | null>(null)

  // Getters
  const isLoggedIn = computed(() => !!user.value)


  const fetchUser = async () => {
    try {
      user.value = await $fetch('/api/auth/me')

      // Set PostHog user identification and properties when user is authenticated
      if (user.value) {
        await $i18n.setLocale(user.value.default_locale)

        const { $posthog } = useNuxtApp()
        if ($posthog) {
          const displayName = [user.value.first_name, user.value.last_name]
            .filter(Boolean)
            .join(' ')
            .trim()
            || user.value.email
            || user.value.phone_number

          await $posthog.identify(user.value.id, {
            email: user.value.email,
            phone_number: user.value.phone_number,
            first_name: user.value.first_name,
            last_name: user.value.last_name,
            person_display_name: displayName,
            name: displayName,
            role: user.value.role,
            created_at: user.value.created_at
          })
        }
      }
    } catch {
      // ignore error
    }
  }

  const logout = async () => {
    await $fetch('/api/auth/logout', { method: 'POST' })
    user.value = null

    // Reset PostHog user identification on logout
    const { $posthog } = useNuxtApp()
    if ($posthog) {
      $posthog.reset()
    }
  }

  const verifyOtp = async (phoneNumber: string, code: string) => {
    await $fetch('/api/auth/sms/verify', {
      method: 'POST',
      headers:{
        'Content-Type': 'application/json'
      },
      body: {
        phone_number: phoneNumber,
        code: code
      }
    })
  }

  const updatePreferences = async (preferences: { default_locale?: string, default_agencies?: string[], telegram_bot_token?: string, telegram_group_id?: string, telegram_topic_id?: string }) => {
    try {
      const updatedUser = await $fetch('/api/auth/me', {
        method: 'PUT',
        body: preferences
      })
      user.value = updatedUser as UserDto
    } catch (error) {
      console.error('Failed to update preferences:', error)
    }
  }

  const getUser = async () : Promise<UserDto|null> => {
    if (user.value) {
      return user.value
    }

    await fetchUser()

    return user.value
  }

  return {
    // user: readonly(user),
    getUser,
    isLoggedIn,
    fetchUser,
    verifyOtp,
    logout,
    updatePreferences
  }
})
